import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useSoraJobs } from '../hooks/useSoraJobs';
import {
  deleteJob,
  getSoraPricing,
  getSoraPreview,
  getJobStatus,
  requestNoWatermark,
  upscaleFromUrl,
} from '../services/soraService';
import JobCard from '../components/common/JobCard';
import { useTranslation } from 'react-i18next';
import pendingUrlService from '../services/pendingUrlService';
import analyticsService from '../services/analyticsService';
import { downloadJobResult } from '../utils/downloadMedia';

const SORA_URL_REGEX = /^https?:\/\/(?:www\.)?sora\.chatgpt\.com\/p\/[a-z0-9_-]+/i;

// Extract sora_id from URL (e.g., "s_abc123" from "https://sora.chatgpt.com/p/s_abc123")
const extractSoraIdFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/sora\.chatgpt\.com\/p\/([a-z0-9_-]+)/i);
  return match ? match[1] : null;
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m${secs.toString().padStart(2, '0')}s`;
};

const buildPreviewUrl = (value) => {
  // Disable fallback to oscdn2 (clean video) to ensure we show watermarks if available via backend
  return null;
};

const parseAspectRatioValue = (value) => {
  if (!value) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.match(/^(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)/);
    if (match) {
      const first = parseFloat(match[1]);
      const second = parseFloat(match[2]);
      if (first > 0 && second > 0) {
        return first / second;
      }
    }
    const numeric = parseFloat(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return null;
};

const Breadcrumb = () => {
  const { t } = useTranslation('upscale');
  return (
    <p className="text-xs uppercase tracking-[0.35em] text-indigo-500 font-semibold">
      {t('breadcrumb')}
    </p>
  );
};


const VideoSummaryCard = ({
  info,
  onUpscale,
  onNoWatermark,
  upscaleCost,
  watermarkCost,
  processing,
  canUpscale,
  canNoWatermark,
  hasSubscription,
  freeSlotAvailable,
  canPayWatermark,
  upscaleButtonState,
  noWatermarkButtonState,
  onScrollToJobs,
}) => {
  const { t } = useTranslation('upscale');
  const navigate = useNavigate();
  const [realDuration, setRealDuration] = useState(null);
  const [timeToReset, setTimeToReset] = useState('');

  useEffect(() => {
    if (hasSubscription || freeSlotAvailable) return;

    const updateTimer = () => {
      const now = new Date();
      const nextMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0));
      const diff = nextMidnight - now;

      if (diff <= 0) {
        setTimeToReset('00:00:00');
        return;
      }

      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeToReset(`${h}h ${m}m ${s}s`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [hasSubscription, freeSlotAvailable]);

  const handleNoWatermarkClick = () => {
    if (!canNoWatermark && !hasSubscription && !freeSlotAvailable) {
      navigate('/credits');
      return;
    }
    onNoWatermark();
  };

  const handleLoadedMetadata = (e) => {
    const video = e.target;
    if (video.duration && Number.isFinite(video.duration)) {
      setRealDuration(formatDuration(video.duration));
    }
  };

  if (!info) return null;

  // Calculate aspect ratio for container
  const ratio = info.aspectRatio || (info.width && info.height ? info.width / info.height : 16/9);
  const isVertical = ratio < 1;
  
  // Dynamic container style
  const containerStyle = {
    aspectRatio: `${ratio}`,
  };

  // Determine if we should show the timer (No free slot AND No credits)
  const showTimer = !hasSubscription && !freeSlotAvailable && !canPayWatermark;

  return (
    <div className="relative w-full overflow-hidden rounded-3xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-2xl ring-1 ring-slate-200 dark:ring-white/10 transition-all duration-500 animate-in fade-in slide-in-from-bottom-4">
      {/* Ambient Background with Blur */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden rounded-3xl">
        {info.thumbnailUrl && (
          <img
            src={info.thumbnailUrl}
            alt="Background"
            className="h-full w-full object-cover opacity-20 dark:opacity-30 blur-2xl scale-125"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/95 to-white dark:from-slate-950/90 dark:via-slate-950/95 dark:to-slate-950" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 grid gap-8 p-6 md:p-8 lg:grid-cols-2 items-center">
        {/* Left Column: Video Player */}
        <div className="flex flex-col gap-4 w-full items-center justify-center">
          <div 
            className={`group relative w-full overflow-hidden rounded-2xl bg-slate-100 dark:bg-black/50 shadow-lg ring-1 ring-black/5 dark:ring-white/10 transition-all duration-500 mx-auto ${isVertical ? 'max-w-[200px] shadow-2xl' : 'max-w-full'}`}
            style={containerStyle}
          >
            {info.previewUrl ? (
              <video
                src={info.previewUrl}
                className="h-full w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                controls
                onLoadedMetadata={handleLoadedMetadata}
              />
            ) : info.thumbnailUrl ? (
              // Show thumbnail if video preview not available
              <div className="relative h-full w-full">
                <img 
                  src={info.thumbnailUrl} 
                  alt="Video thumbnail"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="text-center text-white">
                    <FontAwesomeIcon icon="video" className="text-3xl mb-2 opacity-80" />
                    <p className="text-xs opacity-70">Aperçu non disponible</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center text-slate-400">
                <FontAwesomeIcon icon="video" className="text-4xl opacity-50 mb-2" />
                <p className="text-xs opacity-70">Aperçu non disponible</p>
              </div>
            )}
            {/* Resolution Badge Overlay */}
            <div className="absolute top-3 left-3 flex gap-2">
              {info.watermarkDetected && (
                <span className="px-2 py-1 rounded-md bg-rose-500/90 backdrop-blur-md text-[10px] font-bold text-white border border-white/10 shadow-sm">
                  Watermark
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Actions & Info */}
        <div className="flex flex-col justify-center gap-6 h-full">
          <div className="space-y-2">
            <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-tight">
              {info.title || 'Vidéo Sora détectée'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              {info.description ||
                'Cette vidéo est prête à être améliorée. Choisissez votre workflow ci-dessous.'}
            </p>
            
            {/* Tech Specs moved here for better flow */}
            <div className="flex flex-wrap gap-2 pt-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-xs font-medium text-slate-600 dark:text-slate-300">
                <FontAwesomeIcon icon="clock" className="text-indigo-500 dark:text-indigo-400 text-[10px]" />
                {realDuration || info.duration || '—'}
                </div>
            </div>
          </div>

          <div className="grid gap-3 pt-2">
            {/* Upscale Action - Primary */}
            {(() => {
              const isDisabled = !canUpscale || processing.upscale || upscaleButtonState.disabled;
              const isCompleted = upscaleButtonState.reason === 'completed';
              const isInProgress = upscaleButtonState.reason === 'in_progress';
              const isFailed = upscaleButtonState.reason === 'failed';
              
              return (
                <button
                  type="button"
                  onClick={isCompleted || isInProgress ? onScrollToJobs : onUpscale}
                  disabled={isDisabled && !isCompleted && !isInProgress}
                  className={`group relative flex items-center justify-between overflow-hidden rounded-xl p-4 text-left transition-all duration-300 ${
                    isCompleted
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md cursor-pointer hover:shadow-lg hover:scale-[1.01]'
                      : isInProgress
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md cursor-pointer hover:shadow-lg hover:scale-[1.01]'
                      : processing.upscale
                      ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white cursor-wait shadow-lg shadow-indigo-500/25'
                      : !canUpscale
                      ? 'opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-800/50'
                      : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white shadow-md hover:shadow-lg hover:shadow-indigo-500/25 hover:scale-[1.01] active:scale-[0.99]'
                  }`}
                >
                  {/* Progress bar overlay */}
                  {processing.upscale && !isCompleted && !isInProgress && (
                    <div className="absolute inset-0 overflow-hidden">
                      <div 
                        className={`absolute inset-y-0 left-0 bg-white/10 transition-all duration-700 ease-out ${
                          processing.upscalePhase === 'analyzing' ? 'w-1/3' :
                          processing.upscalePhase === 'sending' ? 'w-2/3' :
                          processing.upscalePhase === 'done' ? 'w-full' : 'w-0'
                        }`}
                      />
                    </div>
                  )}
                  
                  <div className="relative flex items-center gap-4">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-300 ${
                      isCompleted
                        ? 'bg-white/20'
                        : isInProgress
                        ? 'bg-white/20'
                        : !canUpscale && !processing.upscale 
                        ? 'bg-slate-200 dark:bg-white/10' 
                        : processing.upscalePhase === 'done'
                        ? 'bg-emerald-400/30'
                        : 'bg-white/20'
                    }`}>
                      {isCompleted ? (
                        <FontAwesomeIcon icon="check-circle" className="text-lg" />
                      ) : isInProgress ? (
                        <FontAwesomeIcon icon="spinner" spin className="text-lg" />
                      ) : processing.upscalePhase === 'analyzing' ? (
                        <FontAwesomeIcon icon="magnifying-glass" className="text-lg animate-pulse" />
                      ) : processing.upscalePhase === 'sending' ? (
                        <FontAwesomeIcon icon="paper-plane" className="text-lg animate-bounce" />
                      ) : processing.upscalePhase === 'done' ? (
                        <FontAwesomeIcon icon="check" className="text-lg text-emerald-300" />
                      ) : (
                        <FontAwesomeIcon icon="infinity" className="text-lg" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-base transition-all duration-200">
                        {isCompleted ? t('ui.completed') :
                         isInProgress ? t('ui.inProgress') :
                         isFailed ? t('ui.retry') :
                         processing.upscalePhase === 'analyzing' ? t('ui.analyzing') :
                         processing.upscalePhase === 'sending' ? t('ui.sending') :
                         processing.upscalePhase === 'done' ? t('ui.created') :
                         t('ui.titleDefault')}
                      </p>
                      <p className={`text-xs transition-all duration-200 ${
                        isCompleted || isInProgress 
                          ? 'text-white/80' 
                          : !canUpscale && !processing.upscale 
                          ? 'text-slate-500 dark:text-slate-400' 
                          : 'text-indigo-100'
                      }`}>
                        {isCompleted ? t('ui.subtitleCompleted') :
                         isInProgress ? t('ui.subtitleProgress') :
                         isFailed ? t('ui.subtitleFail') :
                         processing.upscalePhase === 'analyzing' ? t('ui.subtitleAnalyze') :
                         processing.upscalePhase === 'sending' ? t('ui.subtitleSend') :
                         processing.upscalePhase === 'done' ? t('ui.subtitleRedirect') :
                         <><span className="line-through opacity-60">720p</span> → 1440p • {t('cards.quota.paid')}</>}
                      </p>
                    </div>
                  </div>
                  <div className="relative text-right">
                    <div className={`font-bold text-sm transition-all duration-200 ${
                      isCompleted || isInProgress
                        ? 'text-white'
                        : !canUpscale && !processing.upscale 
                        ? 'text-slate-600 dark:text-slate-300' 
                        : 'text-white'
                    }`}>
                      {isCompleted ? (
                        <span className="flex items-center gap-1.5">
                          <FontAwesomeIcon icon="arrow-down" />
                          <span className="text-xs">Voir</span>
                        </span>
                      ) : isInProgress ? (
                        <span className="text-xs">En cours</span>
                      ) : processing.upscale ? (
                        <span className="inline-flex items-center gap-2">
                          {processing.upscalePhase === 'done' ? (
                            <FontAwesomeIcon icon="arrow-down" className="animate-bounce" />
                          ) : (
                            <span className="text-xs opacity-80">
                              {processing.upscalePhase === 'analyzing' ? '1/3' : '2/3'}
                            </span>
                          )}
                        </span>
                      ) : (
                        `${upscaleCost} crédits`
                      )}
                    </div>
                  </div>
                </button>
              );
            })()}

            {/* No Watermark Action - Secondary */}
            {(() => {
              const isDisabled = processing.free || noWatermarkButtonState.disabled;
              const isCompleted = noWatermarkButtonState.reason === 'completed';
              const isInProgress = noWatermarkButtonState.reason === 'in_progress';
              const isFailed = noWatermarkButtonState.reason === 'failed';
              
              return (
                <button
                  type="button"
                  onClick={isCompleted || isInProgress ? onScrollToJobs : handleNoWatermarkClick}
                  disabled={isDisabled && !isCompleted && !isInProgress}
                  className={`group relative flex items-center justify-between overflow-hidden rounded-xl p-4 text-left transition-all duration-300 border ${
                    isCompleted
                      ? 'border-emerald-300 dark:border-emerald-500/50 bg-emerald-50 dark:bg-emerald-500/10 cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-500/20'
                      : isInProgress
                      ? 'border-amber-300 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-500/10 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-500/20'
                      : processing.free
                      ? 'border-indigo-300 dark:border-indigo-500/50 bg-indigo-50 dark:bg-indigo-500/10'
                      : freeSlotAvailable && !hasSubscription
                      ? 'border-emerald-300 dark:border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-500/5 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 hover:border-emerald-400 dark:hover:border-emerald-500/60'
                      : !canNoWatermark && !showTimer
                      ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800/30 border-slate-200 dark:border-white/10'
                      : 'bg-white dark:bg-slate-800/40 border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 hover:border-slate-300 dark:hover:border-white/20'
                  }`}
                >
                  {/* Free slot indicator pulse */}
                  {freeSlotAvailable && !hasSubscription && !processing.free && !isCompleted && !isInProgress && (
                    <div className="absolute top-2 right-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                    </div>
                  )}
                  
                  {/* Progress bar overlay */}
                  {processing.free && !isCompleted && !isInProgress && (
                    <div className="absolute inset-0 overflow-hidden rounded-xl">
                      <div 
                        className={`absolute inset-y-0 left-0 bg-indigo-100 dark:bg-indigo-500/20 transition-all duration-500 ease-out ${
                          processing.freePhase === 'analyzing' ? 'w-1/3' :
                          processing.freePhase === 'sending' ? 'w-2/3' :
                          processing.freePhase === 'done' ? 'w-full' : 'w-0'
                        }`}
                      />
                    </div>
                  )}
                  
                  <div className="relative flex items-center gap-4">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-300 ${
                      isCompleted
                        ? 'bg-emerald-100 dark:bg-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                        : isInProgress
                        ? 'bg-amber-100 dark:bg-amber-500/30 text-amber-600 dark:text-amber-400'
                        : processing.free
                        ? processing.freePhase === 'done' 
                          ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                          : 'bg-indigo-100 dark:bg-indigo-500/30 text-indigo-600 dark:text-indigo-400'
                        : freeSlotAvailable && !hasSubscription
                        ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 group-hover:text-slate-700 dark:group-hover:text-white'
                    }`}>
                      {isCompleted ? (
                        <FontAwesomeIcon icon="check-circle" className="text-lg" />
                      ) : isInProgress ? (
                        <FontAwesomeIcon icon="spinner" spin className="text-lg" />
                      ) : processing.freePhase === 'analyzing' ? (
                        <FontAwesomeIcon icon="magnifying-glass" className="text-lg animate-pulse" />
                      ) : processing.freePhase === 'sending' ? (
                        <FontAwesomeIcon icon="download" className="text-lg animate-bounce" />
                      ) : processing.freePhase === 'done' ? (
                        <FontAwesomeIcon icon="check" className="text-lg" />
                      ) : freeSlotAvailable && !hasSubscription ? (
                        <FontAwesomeIcon icon="gift" className="text-lg" />
                      ) : (
                        <FontAwesomeIcon icon="wand-magic-sparkles" className="text-lg" />
                      )}
                    </div>
                    <div>
                      <p className={`font-bold text-sm transition-colors ${
                        isCompleted 
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : isInProgress 
                          ? 'text-amber-700 dark:text-amber-300'
                          : processing.free 
                          ? 'text-indigo-700 dark:text-indigo-300' 
                          : freeSlotAvailable && !hasSubscription
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white'
                      }`}>
                        {isCompleted ? 'Téléchargement prêt !' :
                         isInProgress ? 'Traitement en cours...' :
                         isFailed ? 'Réessayer' :
                         processing.freePhase === 'analyzing' ? 'Analyse en cours...' :
                         processing.freePhase === 'sending' ? 'Téléchargement...' :
                         processing.freePhase === 'done' ? 'Téléchargement lancé !' :
                         'Retirer Watermark'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {isCompleted ? 'Cliquez pour voir le résultat' :
                         isInProgress ? 'Cliquez pour suivre le statut' :
                         isFailed ? 'Le job précédent a échoué' :
                         processing.freePhase === 'analyzing' ? 'Récupération de la vidéo source' :
                         processing.freePhase === 'sending' ? 'Préparation du fichier...' :
                         processing.freePhase === 'done' ? 'Disponible dans vos jobs' :
                         hasSubscription ? 'Inclus dans ton abonnement' :
                         freeSlotAvailable ? '🎁 1 téléchargement offert par jour' :
                         showTimer ? 'Recharger pour éviter l\'attente' : 
                         'Ton gratuit du jour est utilisé'}
                      </p>
                    </div>
                  </div>
                  <div className="relative text-right">
                    {isCompleted ? (
                      <span className="font-bold text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                        <FontAwesomeIcon icon="arrow-down" />
                        <span>Voir</span>
                      </span>
                    ) : isInProgress ? (
                      <span className="font-bold text-xs text-amber-600 dark:text-amber-400">En cours</span>
                    ) : processing.free ? (
                      <span className={`font-bold text-xs ${
                        processing.freePhase === 'done' 
                          ? 'text-emerald-600 dark:text-emerald-400' 
                          : 'text-indigo-600 dark:text-indigo-400'
                      }`}>
                        {processing.freePhase === 'done' ? (
                          <FontAwesomeIcon icon="check-circle" />
                        ) : (
                          <span>{processing.freePhase === 'analyzing' ? '1/2' : '2/2'}</span>
                        )}
                      </span>
                    ) : (
                      <div className="text-right">
                        {hasSubscription ? (
                          <p className="font-bold text-xs text-emerald-600 dark:text-emerald-400">
                            <FontAwesomeIcon icon="infinity" className="mr-1" />
                            Illimité
                          </p>
                        ) : freeSlotAvailable ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold">
                              <FontAwesomeIcon icon="gift" />
                              OFFERT
                            </span>
                            <span className="text-[9px] text-slate-400">1/jour</span>
                          </div>
                        ) : showTimer ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="text-xs font-bold text-indigo-500">{timeToReset}</span>
                            <span className="text-[9px] text-slate-400">prochain gratuit</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="font-bold text-xs text-slate-700 dark:text-slate-200">{watermarkCost} crédit</span>
                            <span className="text-[9px] text-slate-400">gratuit épuisé</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              );
            })()}
          </div>

          <p className="text-center text-[10px] text-slate-400 dark:text-slate-500">
            <FontAwesomeIcon icon="shield-halved" className="mr-1.5" />
            Les fichiers sont traités sur nos serveurs GPU sécurisés.
          </p>
        </div>
      </div>
    </div>
  );
};

const VideoModal = ({ src, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!src) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-6xl max-h-[90vh] aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-md transition-colors hover:bg-white/20"
        >
          <FontAwesomeIcon icon="xmark" className="text-lg" />
        </button>
        <video 
          src={src} 
          className="h-full w-full object-contain" 
          controls 
          autoPlay 
          playsInline
        />
      </div>
    </div>
  );
};

const MuzoUpscalePage = () => {
  const { t } = useTranslation('upscale');
  const { credits, userProfile, refreshUserProfile } = useAuth();
  const toast = useToast();
  
  // Utiliser le hook RTDB pour les jobs (temps réel, plus de polling)
  const { 
    jobs, 
    loading: jobsLoading, 
    upsertJob, 
    removeJob,
    forceRefresh: refreshJobs 
  } = useSoraJobs();
  
  const [sourceUrl, setSourceUrl] = useState('');
  const [urlTouched, setUrlTouched] = useState(false);
  const [pendingUrlImported, setPendingUrlImported] = useState(false);
  const [fromLandingPage, setFromLandingPage] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [watermarkCost, setWatermarkCost] = useState(1);
  const [videoInfo, setVideoInfo] = useState(null);
  const [processing, setProcessing] = useState({ 
    free: false, 
    upscale: false,
    // Phases: 'idle' | 'analyzing' | 'sending' | 'done'
    freePhase: 'idle',
    upscalePhase: 'idle'
  });
  const [previewStatus, setPreviewStatus] = useState('idle');
  const jobPreviewRef = useRef({});
  const [jobPreviewCache, setJobPreviewCache] = useState({});
  const [refreshingId, setRefreshingId] = useState(null);
  const [fullscreenVideo, setFullscreenVideo] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const jobsSectionRef = useRef(null);
  const [highlightedJobId, setHighlightedJobId] = useState(null);

  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const freeDownloadUsedToday = useMemo(() => {
    const last =
      userProfile?.usage?.lastFreeNowatermarkAt ||
      userProfile?.saas_usage?.quota_no_watermark_last_use ||
      userProfile?.tracking?.last_free_download_at;
    if (!last) return false;
    const lastDate = new Date(last);
    if (Number.isNaN(lastDate.getTime())) return false;
    const now = new Date(nowTick);
    return (
      lastDate.getFullYear() === now.getFullYear() &&
      lastDate.getMonth() === now.getMonth() &&
      lastDate.getDate() === now.getDate()
    );
  }, [userProfile, nowTick]);
  const planState = userProfile?.plan || {};

  const hasSubscription =
    (planState?.type === 'pro' && planState?.status !== 'canceled') ||
    planState?.is_active ||
    userProfile?.saas_usage?.subscription_active ||
    (userProfile?.saas_usage?.subscription_plan_id &&
      userProfile?.saas_usage?.subscription_plan_id !== 'free');

  const freeSlotAvailable = !hasSubscription && !freeDownloadUsedToday;
  const canPayWatermark = (credits ?? 0) >= watermarkCost;
  const canUseFreeDownload = hasSubscription || freeSlotAvailable;
  const canTriggerNoWatermark = canUseFreeDownload || canPayWatermark;

  const upscaleCost = pricing ?? 15; // Fallback: free user price (15 crédits)
  const hasEnoughCredits = (credits ?? 0) >= (upscaleCost || 0);
  const urlValid = SORA_URL_REGEX.test(sourceUrl.trim());
  const showUrlError = urlTouched && !urlValid && sourceUrl.trim().length > 0;

  // Check if there are existing jobs for the current URL
  const currentSoraId = useMemo(() => extractSoraIdFromUrl(sourceUrl), [sourceUrl]);
  
  const existingJobsForUrl = useMemo(() => {
    if (!currentSoraId || !jobs.length) return { upscale: null, noWatermark: null };
    
    const matchingJobs = jobs.filter(job => job.sora_id === currentSoraId);
    
    // Find upscale job - must explicitly have job_type 'upscale' or contain 'upscale' in type
    const upscaleJob = matchingJobs.find(job => {
      const jobType = (job.job_type || '').toLowerCase();
      return jobType.includes('upscale') || jobType === 'sora_upscale_x2';
    });
    
    // Find no_watermark job - must explicitly have job_type 'no_watermark'
    const noWatermarkJob = matchingJobs.find(job => {
      const jobType = (job.job_type || '').toLowerCase();
      return jobType === 'no_watermark';
    });
    
    return { 
      upscale: upscaleJob || null, 
      noWatermark: noWatermarkJob || null 
    };
  }, [currentSoraId, jobs]);

  // Determine button states based on existing jobs
  const upscaleButtonState = useMemo(() => {
    const job = existingJobsForUrl.upscale;
    if (!job) return { disabled: false, reason: null, status: null };
    
    const status = job.status?.toLowerCase();
    if (status === 'completed' || status === 'upscale_completed') {
      return { disabled: true, reason: 'completed', status: 'completed' };
    }
    if (status === 'processing' || status === 'queued' || status === 'in_progress') {
      return { disabled: true, reason: 'in_progress', status };
    }
    if (status === 'failed') {
      return { disabled: false, reason: 'failed', status: 'failed' };
    }
    return { disabled: false, reason: null, status };
  }, [existingJobsForUrl.upscale]);

  const noWatermarkButtonState = useMemo(() => {
    const job = existingJobsForUrl.noWatermark;
    if (!job) return { disabled: false, reason: null, status: null };
    
    const status = job.status?.toLowerCase();
    if (status === 'completed' || status === 'no_watermark_ready') {
      return { disabled: true, reason: 'completed', status: 'completed' };
    }
    if (status === 'processing' || status === 'queued' || status === 'in_progress') {
      return { disabled: true, reason: 'in_progress', status };
    }
    if (status === 'failed') {
      return { disabled: false, reason: 'failed', status: 'failed' };
    }
    return { disabled: false, reason: null, status };
  }, [existingJobsForUrl.noWatermark]);

  const loadPricing = useCallback(async () => {
    const value = await getSoraPricing().catch(() => null);
    if (!value) return;
    if (typeof value === 'number') {
      setPricing(value);
      return;
    }
    if (typeof value === 'object') {
      if (typeof value.upscale2k === 'number') setPricing(value.upscale2k);
      else if (typeof value.price === 'number') setPricing(value.price);
      if (typeof value.watermark_remove === 'number') setWatermarkCost(value.watermark_remove);
    }
  }, []);

  // fetchJobs, upsertJob, refreshJobs sont maintenant fournis par useSoraJobs hook

  const fetchJobPreview = useCallback(async (job) => {
    if (!job?.job_id || !job?.source_url) return;
    if (jobPreviewRef.current[job.job_id]) return;
    jobPreviewRef.current[job.job_id] = { loading: true };
    try {
      const data = await getSoraPreview(job.source_url);
      const payload = {
        thumbnailUrl: data.thumbnail_url || job.thumbnail_url || null,
        previewUrl: data.preview_url || job.preview_url || buildPreviewUrl(job.source_url),
        aspectRatio:
          parseAspectRatioValue(data.aspect_ratio) ||
          data.aspect_ratio ||
          (data.width && data.height
            ? (Number(data.width) || 0) / (Number(data.height) || 1)
            : null),
        width: data.width,
        height: data.height,
      };
      jobPreviewRef.current[job.job_id] = payload;
      setJobPreviewCache((prev) => ({ ...prev, [job.job_id]: payload }));
    } catch (err) {
      console.error('Failed to preload job preview', err);
      const fallbackRatio =
        job.aspectRatio ||
        job.aspect_ratio ||
        (job.output_width && job.output_height
          ? job.output_width / job.output_height
          : null);
      const payload = {
        thumbnailUrl: job.thumbnail_url || null,
        previewUrl: job.preview_url || buildPreviewUrl(job.source_url),
        aspectRatio: fallbackRatio,
      };
      jobPreviewRef.current[job.job_id] = payload;
      setJobPreviewCache((prev) => ({ ...prev, [job.job_id]: payload }));
    }
  }, []);

  const groupedJobs = useMemo(() => {
    const groups = {};
    jobs.forEach((job) => {
      // Use sora_id as primary key, fallback to source_url
      const id = job.sora_id || job.source_url;
      if (!id) return; // Should not happen but safe guard

      if (!groups[id]) {
        groups[id] = {
          id,
          sora_id: job.sora_id,
          source_url: job.source_url,
          jobs: [],
        };
      }
      groups[id].jobs.push(job);
    });

    return Object.values(groups).sort((a, b) => {
      // Sort groups by the most recent job in each group
      const dateA = Math.max(...a.jobs.map((j) => new Date(j.created_at).getTime()));
      const dateB = Math.max(...b.jobs.map((j) => new Date(j.created_at).getTime()));
      return dateB - dateA;
    });
  }, [jobs]);

  // Les jobs sont chargés automatiquement via useSoraJobs (RTDB subscription)
  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  // Check for pending URL from landing page on mount
  useEffect(() => {
    const pendingUrl = pendingUrlService.getPendingUrl();
    if (pendingUrl && !sourceUrl) {
      setSourceUrl(pendingUrl);
      setPendingUrlImported(true);
      setFromLandingPage(true);
      setUrlTouched(true);
      analyticsService.trackPendingUrlPrefilled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount - intentionally excluding sourceUrl to prevent re-runs

  useEffect(() => {
    if (!groupedJobs.length) return;
    // Fetch preview for the latest job of each group to populate thumbnails
    groupedJobs.forEach((group) => {
      const latest = group.jobs.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      )[0];
      if (latest) fetchJobPreview(latest);
    });
  }, [groupedJobs, fetchJobPreview]);

  useEffect(() => {
    const trimmed = sourceUrl.trim();
    if (!SORA_URL_REGEX.test(trimmed)) {
      setVideoInfo(null);
      setPreviewStatus('idle');
      return;
    }
    let cancelled = false;
    setPreviewStatus('loading');

    const fetchPreview = async () => {
      try {
        const data = await getSoraPreview(trimmed);
        if (cancelled) return;
        const ratioFromServer =
          parseAspectRatioValue(data.aspect_ratio) ||
          (data.width && data.height
            ? (Number(data.width) || 0) / (Number(data.height) || 1)
            : null);
        setVideoInfo({
          inputResolution: data.input_resolution || '720p',
          duration: data.duration || '—',
          outputResolution: data.output_resolution || '1440p (2K)',
          aspectRatio: ratioFromServer || data.aspect_ratio || '16:9',
          watermarkDetected: data.watermark_detected ?? true,
          thumbnailUrl: data.thumbnail_url || null,
          previewUrl: data.preview_url || buildPreviewUrl(trimmed),
          title: data.title,
          description: data.description,
          width: data.width,
          height: data.height,
        });
        setPreviewStatus('success');
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load preview', err);
        setPreviewStatus('error');
        setVideoInfo({
          inputResolution: '720p',
          duration: '—',
          outputResolution: '1440p (2K)',
          aspectRatio: '16:9',
          watermarkDetected: true,
          thumbnailUrl: null,
          previewUrl: buildPreviewUrl(trimmed),
        });
      }
    };

    fetchPreview();
    return () => {
      cancelled = true;
    };
  }, [sourceUrl]);

  // Les jobs sont mis à jour en temps réel via RTDB (useSoraJobs), plus besoin de polling

  const handleUrlChange = (value) => {
    setSourceUrl(value);
    setUrlTouched(false);
  };

  const handlePasteFromClipboard = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
      toast.warning('Presse-papiers indisponible.');
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setSourceUrl(text.trim());
        setUrlTouched(false);
        toast.success('Lien colle depuis le presse-papiers.');
      } else {
        toast.info('Presse-papiers vide.');
      }
    } catch (err) {
      console.error('Clipboard error', err);
      toast.error('Impossible de lire le presse-papiers.');
    }
  }, [toast]);

  const ensureValidUrl = (value = sourceUrl) => {
    const stringValue = typeof value === 'string' ? value : value?.toString() || '';
    const trimmed = stringValue.trim();
    if (!trimmed) {
      setUrlTouched(true);
      toast.warning('Merci de coller une URL Sora.');
      return false;
    }
    if (!SORA_URL_REGEX.test(trimmed)) {
      setUrlTouched(true);
      toast.error('Ce lien ne ressemble pas a une URL de video Sora valide.');
      return false;
    }
    return true;
  };

  const handleNoWatermark = async (customUrl) => {
    const targetUrl = typeof customUrl === 'string' ? customUrl : sourceUrl;
    if (!ensureValidUrl(targetUrl)) return;

    const willUseCredits = !hasSubscription && freeDownloadUsedToday;
    if (willUseCredits && !canPayWatermark) {
      toast.warning(t('status.noCredits', { cost: watermarkCost }));
      return;
    }

    // Phase 1: Analyzing
    setProcessing((prev) => ({ ...prev, free: true, freePhase: 'analyzing' }));
    
    // Simulate brief analysis delay for UX feedback
    await new Promise(resolve => setTimeout(resolve, 600));
    
    // Phase 2: Sending
    setProcessing((prev) => ({ ...prev, freePhase: 'sending' }));
    
    try {
      const job = await requestNoWatermark(targetUrl.trim());
      
      // Phase 3: Done
      setProcessing((prev) => ({ ...prev, freePhase: 'done' }));
      
      // Track analytics
      analyticsService.trackJobStarted({
        jobType: 'no_watermark',
        fromLanding: fromLandingPage,
        creditsUsed: willUseCredits ? watermarkCost : 0,
      });
      
      // Clear pending URL if this was from landing page
      if (fromLandingPage) {
        pendingUrlService.clearPendingUrl();
        setFromLandingPage(false);
        setPendingUrlImported(false);
      }
      
      // Refresh user profile to update quota state (prevents UI desync)
      refreshUserProfile?.();
      
      // Brief success state before reset
      await new Promise(resolve => setTimeout(resolve, 800));
      
      upsertJob(job);
      toast.success(
        willUseCredits
          ? t('status.noWatermarkDone', { cost: watermarkCost, plural: watermarkCost > 1 ? 's' : '' })
          : t('status.noWatermarkStarted')
      );
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        t('status.noWatermarkFail');
      toast.error(message);
    } finally {
      setProcessing((prev) => ({ ...prev, free: false, freePhase: 'idle' }));
    }
  };

  const handleUpscale = async (customUrl) => {
    const targetUrl = customUrl ?? sourceUrl;
    if (!ensureValidUrl(targetUrl)) return;
    if (!hasEnoughCredits) {
      toast.warning(t('status.noCredits', { cost: watermarkCost }), {
        action: { label: t('actions.goCredits'), onClick: () => window.location.href = '/credits' }
      });
      return;
    }
    
    // Phase 1: Analyzing
    setProcessing((prev) => ({ ...prev, upscale: true, upscalePhase: 'analyzing' }));
    
    // Simulate brief analysis delay for UX feedback
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Phase 2: Sending
    setProcessing((prev) => ({ ...prev, upscalePhase: 'sending' }));
    
    try {
      const job = await upscaleFromUrl(targetUrl.trim());
      
      // Phase 3: Done
      setProcessing((prev) => ({ ...prev, upscalePhase: 'done' }));
      
      // Track analytics
      analyticsService.trackJobStarted({
        jobType: 'upscale',
        fromLanding: fromLandingPage,
        creditsUsed: upscaleCost,
      });
      
      // Clear pending URL if this was from landing page
      if (fromLandingPage) {
        pendingUrlService.clearPendingUrl();
        setFromLandingPage(false);
        setPendingUrlImported(false);
      }
      
      // Brief success state before reset
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      upsertJob(job);
      toast.success(t('status.upscaleStarted'));
      
      // Highlight the new job and scroll to jobs section
      setHighlightedJobId(job.job_id);
      setTimeout(() => {
        jobsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      // Remove highlight after 3 seconds
      setTimeout(() => setHighlightedJobId(null), 3000);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        'Impossible de lancer l\'upscale.';
      toast.error(message);
    } finally {
      setProcessing((prev) => ({ ...prev, upscale: false, upscalePhase: 'idle' }));
    }
  };

  const handleDownloadJob = async (job) => {
    if (!job.result_url) return;
    setDownloadingId(job.job_id);
    try {
      await downloadJobResult(job);
    } catch (e) {
      // Fallback if fetch fails (e.g. CORS)
      const link = document.createElement('a');
      link.href = job.result_url;
      link.download = `${job.sora_id || 'video'}.mp4`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    setDownloadingId(null);
  };

  const handleCopyLink = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Lien copie !');
    } catch (err) {
      console.error('copy failed', err);
      toast.error('Impossible de copier le lien.');
    }
  };

  const handleDeleteJob = async (job) => {
    if (!job?.job_id) return;
    if (!window.confirm('Supprimer ce job de votre historique ?')) return;
    try {
      await deleteJob(job.job_id);
      removeJob(job.job_id);
      toast.success('Job supprime');
    } catch (err) {
      console.error('delete job error', err);
      toast.error('Impossible de supprimer ce job.');
    }
  };

  const handleRefreshJobStatus = useCallback(
    async (job) => {
      if (!job?.runpod_job_id) return;
      setRefreshingId(job.runpod_job_id);
      try {
        const updated = await getJobStatus(job.runpod_job_id);
        upsertJob(updated);
        toast.info('Statut mis a jour');
      } catch (err) {
        console.error('Failed to refresh job status', err);
        const message =
          err?.response?.data?.detail || err?.message || 'Impossible de rafraichir le statut.';
        toast.error(message);
      } finally {
        setRefreshingId(null);
      }
    },
    [upsertJob, toast]
  );

  const handleRetryJob = (job) => {
    // Reconstruct original Sora URL from sora_id if source_url is a CDN URL
    let retryUrl = job?.source_url;
    
    // If source_url is a CDN URL or invalid, reconstruct from sora_id
    if (job?.sora_id && job.sora_id.startsWith('s_')) {
      const soraUrl = `https://sora.chatgpt.com/p/${job.sora_id}`;
      // Use reconstructed URL if source_url doesn't look like a valid Sora URL
      if (!retryUrl || !SORA_URL_REGEX.test(retryUrl)) {
        retryUrl = soraUrl;
      }
    }
    
    if (!retryUrl || !SORA_URL_REGEX.test(retryUrl)) {
      toast.error('Impossible de réessayer : URL source invalide.');
      return;
    }
    
    setSourceUrl(retryUrl);
    setUrlTouched(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => handleUpscale(retryUrl), 300);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white">
      <VideoModal src={fullscreenVideo} onClose={() => setFullscreenVideo(null)} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <div className="space-y-2 text-center">
          <Breadcrumb />
          <h1 className="text-3xl sm:text-4xl font-bold">{t('pageTitle')}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 max-w-2xl mx-auto">
            {t('pageSubtitle')}
          </p>
        </div>

        {/* Input Section */}
        <section className="mx-auto w-full max-w-4xl space-y-4">
          <div className="relative rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 p-2 shadow-xl transition-all focus-within:ring-2 focus-within:ring-indigo-500/50">
            <div className="flex items-center gap-2">
              <div className="pl-4 text-slate-400">
                <FontAwesomeIcon icon="link" />
              </div>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => handleUrlChange(e.target.value)}
                onBlur={() => setUrlTouched(true)}
                placeholder="https://sora.chatgpt.com/p/s_6915a1e0..."
                className="flex-1 bg-transparent px-4 py-4 text-base sm:text-lg outline-none placeholder:text-slate-400 dark:text-white"
              />
              <button
                type="button"
                onClick={handlePasteFromClipboard}
                className="mr-2 hidden sm:inline-flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/20"
              >
                <FontAwesomeIcon icon="copy" />
                Coller
              </button>
            </div>
          </div>

          {/* Feedback & Status */}
          <div className="min-h-[24px] px-4 text-center">
            {pendingUrlImported && (
              <p className="text-sm text-emerald-500 dark:text-emerald-400 font-medium flex items-center justify-center gap-2">
                <FontAwesomeIcon icon="check-circle" />
                {t('prefill.imported', { ns: 'landing' })}
              </p>
            )}
            {previewStatus === 'loading' && (
              <p className="text-sm text-slate-500 dark:text-slate-400 animate-pulse">
                <FontAwesomeIcon icon="spinner" spin className="mr-2" />
                Analyse du lien Sora en cours...
              </p>
            )}
            {previewStatus === 'error' && (
              <p className="text-sm text-rose-500">
                Impossible de récupérer l'aperçu. Le mode basique sera utilisé.
              </p>
            )}
            {showUrlError && (
              <p className="text-sm font-medium text-rose-500">
                Ce lien ne ressemble pas à une URL de vidéo Sora valide.
              </p>
            )}
          </div>
        </section>

        {/* Main Content Area: Preview OR Quick Actions Info */}
        <div className="mx-auto w-full max-w-4xl">
          {videoInfo ? (
            <VideoSummaryCard
              info={videoInfo}
              onUpscale={() => handleUpscale()}
              onNoWatermark={() => handleNoWatermark()}
              upscaleCost={upscaleCost}
              watermarkCost={watermarkCost}
              processing={processing}
              canUpscale={hasEnoughCredits}
              canNoWatermark={canTriggerNoWatermark}
              hasSubscription={hasSubscription}
              freeSlotAvailable={freeSlotAvailable}
              canPayWatermark={canPayWatermark}
              upscaleButtonState={upscaleButtonState}
              noWatermarkButtonState={noWatermarkButtonState}
              onScrollToJobs={() => jobsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            />
          ) : (
            <div className="space-y-6">
              <div className="text-center space-y-1">
                <p className="text-xs uppercase tracking-[0.35em] text-indigo-500 font-semibold">
                  Fonctionnalités
                </p>
                <h2 className="text-2xl font-semibold">2 workflows, un même lien.</h2>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                {[
                  {
                    id: 'free',
                    title: hasSubscription
                      ? 'Sans watermark illimité'
                      : 'Télécharger sans watermark',
                    subtitle: hasSubscription
                      ? 'Inclus dans ton plan Muzo+'
                      : freeSlotAvailable
                      ? 'Gratuit - 1/jour'
                      : `${watermarkCost} crédit requis`,
                    badge: hasSubscription ? 'Illimité Muzo+' : null,
                    icon: 'wand-magic-sparkles',
                    helper: hasSubscription
                      ? 'Watermark retiré en continu'
                      : 'Ton quota passe à payant après utilisation quotidienne',
                    color: 'bg-white/90 dark:bg-slate-900/50',
                  },
                  {
                    id: 'upscale',
                    title: 'Upscale ×2 sans watermark',
                    subtitle: `${upscaleCost || 'N/A'} crédits`,
                    badge: 'Temps estimé: ~1 min',
                    icon: 'bolt',
                    helper: '720p → 1440p, watermark retiré et stabilisation incluse',
                    color: 'bg-indigo-600/10 dark:bg-indigo-500/10',
                  },
                ].map((option) => (
                  <article
                    key={option.id}
                    className={`rounded-2xl border border-white/50 dark:border-white/10 p-6 flex flex-col gap-4 ${option.color}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-2xl bg-white/70 dark:bg-white/10 text-indigo-600 dark:text-indigo-300 flex items-center justify-center text-xl shadow-sm">
                        <FontAwesomeIcon icon={option.icon} />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {option.subtitle}
                        </p>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                          {option.title}
                        </p>
                      </div>
                    </div>
                    {option.badge && (
                      <span className="inline-flex items-center gap-2 text-xs font-semibold text-indigo-600 dark:text-indigo-200">
                        <FontAwesomeIcon icon="circle-info" />
                        {option.badge}
                      </span>
                    )}
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {option.helper}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>

        <section className="space-y-3" ref={jobsSectionRef}>
          <div>
            <h2 className="text-2xl font-semibold">Mes derniers jobs</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Historique personnel</p>
            <button
              type="button"
              onClick={refreshJobs}
              className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/40 rounded-full px-3 py-1 hover:bg-indigo-50 dark:hover:bg-white/5"
            >
              <FontAwesomeIcon icon="sync" className="text-[11px]" spin={jobsLoading} />
              Rafraîchir
            </button>
          </div>
          {jobsLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((key) => (
                <div key={key} className="h-32 rounded-3xl bg-slate-100 animate-pulse dark:bg-white/5" />
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 dark:border-white/10 bg-white/60 dark:bg-slate-900/30 p-6 text-center text-sm text-slate-500 dark:text-slate-300">
              Aucun job encore sauvegardé pour cet utilisateur.
              <br />
              <button
                type="button"
                className="mt-2 text-indigo-600 dark:text-indigo-300 font-semibold"
                onClick={handleUpscale}
              >
                {t('inputs.empty')}
              </button>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-auto pr-1">
              {groupedJobs.map((group) => (
                <JobCard
                  key={group.id}
                  group={group}
                  onDownload={handleDownloadJob}
                  onCopy={handleCopyLink}
                  onDelete={handleDeleteJob}
                  onRetry={handleRetryJob}
                  onUpscale={handleUpscale}
                  downloadingId={downloadingId}
                  upscaleCost={upscaleCost}
                  isHighlighted={group.jobs.some(j => j.job_id === highlightedJobId)}
                  previewData={
                    jobPreviewCache[group.jobs[0]?.job_id] ||
                    jobPreviewCache[group.sora_id] ||
                    jobPreviewCache[group.source_url]
                  }
                  onHoverPreview={(job) => fetchJobPreview(job)}
                  onRefreshStatus={handleRefreshJobStatus}
                  refreshingId={refreshingId}
                  onExpand={(url) => setFullscreenVideo(url)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default MuzoUpscalePage;
